/* eslint-disable no-underscore-dangle */
import {
    AfterViewChecked,
    AfterViewInit,
    Component,
    OnInit,
    ViewChild
} from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import { ModuleName } from '../core-data-model';
import {
    SiteNavigationService,
    TableManagerDataService,
    UsersTableData
} from '../core-services';
import { ConfirmationHelpers } from '../modals';
import {
    InitDataService,
    ModuleAccessService,
    ModuleUserAccessService
} from '../system-services';
import { TranslationService } from '../translations';
import { ModuleUserManagementTabComponent } from './module-user-management-tab/module-user-management-tab.component';

@Component({
    selector: 'pi-module-user-management-page',
    template: `
        <mat-tab-group
            #matTabGroup
            animationDuration="0ms"
            dynamicHeight
            [selectedIndex]="activeTab"
            class="sideNav-padding--side"
        >
            @for (tab of availableTabs; track tab) {
                <mat-tab>
                    <ng-template mat-tab-label>{{ tab }}</ng-template>
                    <ng-template matTabContent>
                        <pi-module-user-management-tab
                            [moduleName]="tab"
                            #moduleUserManagementComponent
                        >
                        </pi-module-user-management-tab>
                    </ng-template>
                </mat-tab>
            }
        </mat-tab-group>

        <div
            style="margin-top: 60px;"
            class="sideNav-padding--side sideNav-padding--bottom"
        >
            <h4>
                {{ TR.key('userAdministration.thirdPartyUsers-title') }}
            </h4>
            <third-party-users-table
                [readOnly]="!this.allowThirdPartyAccess"
            ></third-party-users-table>
            <pi-pager
                [tableData]="thirdPartyUsersTableData"
                class="ok-pager"
            ></pi-pager>
        </div>
    `,
    styles: [
        `
            .invitation-block {
                width: 100%;
                background-color: #eee;
            }
            .sideNav-padding--side {
                padding-left: 20px;
                padding-right: 20px;
            }
            .sideNav-padding--bottom {
                padding-bottom: 20px;
            }
            ::ng-deep .mat-tab-label-content {
                font-weight: bold;
            }
            /* override invitation select panels */
            ::ng-deep .user-management-select-panel {
                min-width: calc(100% + 18px) !important;
                margin-left: -1px;
            }
        `
    ],
    standalone: false
})
export class ModuleUserManagementPageComponent
    implements OnInit, AfterViewChecked, AfterViewInit
{
    @ViewChild('matTabGroup') tabs: MatTabGroup;
    @ViewChild('moduleUserManagementComponent')
    moduleUserManagementComponent: ModuleUserManagementTabComponent;
    selectedModule: ModuleName;
    availableTabs: ModuleName[] = [];
    allowThirdPartyAccess = false;

    activeTab: number;
    thirdPartyUsersTableData: UsersTableData;

    constructor(
        public TR: TranslationService,
        private tableManagerService: TableManagerDataService,
        public moduleUserAccess: ModuleUserAccessService,
        public siteNavigationService: SiteNavigationService,
        public moduleService: ModuleAccessService,
        public initDataService: InitDataService
    ) {
        this.thirdPartyUsersTableData =
            this.tableManagerService.makeUsersTable(true);
    }

    ngAfterViewChecked(): void {
        this.tabs.realignInkBar();
    }

    ngAfterViewInit(): void {
        this.registerTabClick();
    }

    ngOnInit() {
        this.thirdPartyUsersTableData.search();
        this.availableTabs = this.getAvailableModules();

        this.selectedModule = this.currentModule();
        this.activeTab = this.availableTabs.indexOf(this.selectedModule);

        this.allowThirdPartyAccess = this.initDataService.isSuperAdmin()
            ? true
            : this.moduleUserAccess.hasModuleAccessLevelOrAbove(
                  this.selectedModule,
                  PermissionsModuleAccessLevel.AllModuleAdmin
              );
    }

    currentModule() {
        if (this.siteNavigationService.isInDesign) {
            return ModuleName.Design;
        } else if (this.siteNavigationService.isInMain) {
            return ModuleName.Diagnose;
        } else if (this.siteNavigationService.isInInspire2) {
            return ModuleName.Inspire;
        }
        return ModuleName.Hire;
    }

    getAvailableModules() {
        const availableModules: ModuleName[] = [];

        // Add the modules, order added is the order that it shows up to the
        // user. Design -> Hire -> Inspire -> Diagnose

        this.addToModules(
            availableModules,
            ModuleName.Design,
            this.moduleService.hasDesign
        );
        this.addToModules(
            availableModules,
            ModuleName.Hire,
            this.moduleService.hasHireHero
        );
        this.addToModules(
            availableModules,
            ModuleName.Inspire,
            this.moduleService.hasInspire2
        );
        this.addToModules(
            availableModules,
            ModuleName.Diagnose,
            this.moduleService.hasDiagnose2
        );

        return availableModules;
    }

    // Registers a private event handler _handleClick from _MatTabGroupBase to intercept a Tab click.
    // MatTabGroup's "selectedTabChange" is triggered too late to be able to detect the state of the current tab before switching.
    async registerTabClick() {
        // Get the handler reference
        const handleTabClick = this.tabs._handleClick;

        // register the event on init to check for unfinished invites
        this.tabs._handleClick = async (tab, header, index) => {
            const hasUnfinishedInvites =
                this.moduleUserManagementComponent.invitationInput
                    ?.piUserTypeahead?.selectedUsers.length > 0;

            if (hasUnfinishedInvites && index !== this.activeTab) {
                await ConfirmationHelpers.generateConfirmationModal(
                    this.TR.key('userManagement.unfinishedInviteWarning.text'),
                    this.TR.key(
                        'userManagement.unfinishedInviteWarning.subtext'
                    ),
                    {
                        yes: 'text.common-terms.continue',
                        no: 'text.common-terms.no'
                    }
                );
            }

            // if the user clicks continue the 'apply' method below completes the event, otherwise the event is canceled
            handleTabClick.apply(this.tabs, [tab, header, index]);

            // update the currentIndex, as we need it again when another tab is clicked
            this.activeTab = index;
        };
    }

    /**
     * Adds the provided module to the provided array of modules if the
     * provided condition is true, and the standardized permissions return true.
     */
    private addToModules(
        modules: ModuleName[],
        module: ModuleName,
        condition: boolean
    ) {
        if (
            condition &&
            (this.initDataService.isSuperAdmin() ||
                this.moduleUserAccess.hasModuleAccessLevelOrAbove(
                    module,
                    PermissionsModuleAccessLevel.NoAccess
                ))
        ) {
            modules.push(module);
        }
    }

    // Returns the <mat-tab-body-content> with the tab content scroll position given the target tab index
    private getTabContentElement(tabIndex: number) {
        return document.getElementsByClassName('mat-tab-body-content')[
            tabIndex
        ];
    }
}
